<template>
  <div
    v-if="isTreatwellLoginPage"
    :class="[$style.split, { [$style.smallScreen]: $screen === 's' }]"
  >
    <TheGlobalModals />
    <div :class="$style.splitLeft">
      <div :class="$style.splitLeftHeader">
        <BaseDropdown
          v-model="activeLanguage"
          :options="languageOptions"
          @update:modelValue="switchLanguage"
          v-test="'language-selector'"
        />
      </div>
      <div :class="$style.splitLeftContent">
        <img
          src="/img/signup/te_full_pink.svg"
          alt="Treatwell Expert logo"
          :class="$style.splitLogo"
        >
        <div :class="$style.splitCard">
          <router-view />
        </div>
      </div>
    </div>
    <div
      v-if="$screen !== 's'"
      :class="$style.splitRight"
    >
      <img
        src="/img/signup/bg-tw.jpg"
        alt="Treatwell Expert background"
        :class="$style.splitImage"
      >
    </div>
  </div>
  <div
    v-else
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's',
        [$style.largeSizePage]: largeSizePage
      }
    ]"
  >
    <TheGlobalModals />
    <div
      ref="scrollContainer"
      :class="$style.inner"
      v-test="'app-scrollcontainer'"
    >
      <div :class="$style.header">
        <a
          v-if="!config.isTreatwellDomain"
          :href="$t('global.corporate_site_url')"
        >
          <SalonizedLogo />
        </a>
        <BaseDropdown
          v-model="activeLanguage"
          :options="languageOptions"
          @update:modelValue="switchLanguage"
          v-test="'language-selector'"
        />
      </div>
      <template v-if="config.isTreatwellDomain && route.name !== 'signup'">
        <img
          src="/img/signup/te_full.svg"
          alt="Treatwell Expert"
          :class="[$style.treatwellLogo, $style.light]"
        >
        <img
          src="/img/signup/te_full_pink.svg"
          alt="Treatwell Expert"
          :class="[$style.treatwellLogo, $style.dark]"
        >
      </template>
      <div :class="$style.content">
        <div :class="$style.page">
          <router-view />
        </div>
      </div>
    </div>
    <Background />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import TheGlobalModals from '@/app/logged-in/global-modals/index.vue';
import config from '@/config';
import { hostBrand, setPageMeta } from '@/helpers/page-meta';
import { usePageScroll } from '@/helpers/scroll';
import router from '@/router';
import Background from './Background.vue';
import SalonizedLogo from './SalonizedLogo.vue';

const scrollContainer = ref();
usePageScroll(scrollContainer);

const { locale, t } = useI18n();

const languageOptions = computed(() =>
  config.locales.map((lang) => ({ label: lang.toUpperCase(), value: lang })),
);

const route = useRoute();
const activeLanguage = ref(locale.value.slice(0, 2));

const switchLanguage = () => {
  router.replace({
    name: route.name,
    params: { locale: activeLanguage.value },
  });
};

provide('utmAttributes', {
  campaign: route.query.utm_campaign || '',
  content: route.query.utm_content || '',
  medium: route.query.utm_medium || '',
  source: route.query.utm_source || '',
  term: route.query.utm_term || '',
});

provide('campaignAttributes', {
  gadCampaignid: route.query.gad_campaignid || '',
});

// Salesforce Field Sales sign-up links carry at most one of these 18-char ids.
provide('salesforceAttributes', {
  leadId: route.query.leadId || '',
  opportunityId: route.query.opportunityId || '',
});

const largeSizePage = computed(() => route.name === 'wavy-onboarding');

const isTreatwellLoginPage = computed(
  () => config.isTreatwellDomain && route.name === 'login',
);

// Search engines index these two pages, the others get the brand's home title
const indexedPages = ['login', 'signup'];

watch(
  [() => route.name, locale],
  ([name]) => {
    const page = String(name);
    setPageMeta(
      indexedPages.includes(page)
        ? {
            title: t(`${page}.page_title`),
            description: t(`${page}.page_description`, {
              brandName: hostBrand.name,
            }),
          }
        : {},
    );
  },
  { immediate: true },
);
</script>

<script lang="ts">
export default {
  name: 'LoggedOut',
};
</script>

<style
  lang="scss"
  module
>
$headerHeight: 52px;

.base {
  height: 100%;
  &.smallScreen {
    margin: 0;
  }
}

.inner {
  position: relative;
  z-index: 1;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing * 2 $spacing * 2 0;
  margin-bottom: $spacing * 2;

  .base.smallScreen & {
    padding: $spacing * 2 $spacing 0;
  }
}

.treatwellLogo {
  margin: 0 auto $spacing;

  .base.smallScreen & {
    width: 300px;
  }

  &.light {
    @include theme('dark') {
      display: none;
    }
  }

  &.dark {
    @include theme('light') {
      display: none;
    }
  }
}

.content {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  .base:not(.base.smallScreen) & {
    padding-bottom: 75px;
  }

  .base.smallScreen & {
    width: 100%;
    padding: $spacing * 2 $spacing;
  }
}

.page {
  max-width: 100%;
  background-color: var(--color-bg);
  border-radius: $radius;
  box-shadow: var(--shadow);

  .base.smallScreen & {
    max-width: 90vw;
  }

  .base:not(.largeSizePage) & {
    width: 520px;
  }

  .base.smallScreen:not(.largeSizePage) & {
    padding: $spacing * 2 $spacing;
  }

  .base:not(.smallScreen):not(.largeSizePage) & {
    padding: $spacing * 2;
  }

  .base.largeSizePage & {
    width: 980px;
  }
}

.split {
  display: flex;
  height: 100%;
}

.splitLeft {
  display: flex;
  flex-direction: column;
  flex: 0 0 50%;
  padding: $spacing * 4;
  min-width: 450px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @include theme('light') {
    background-color: var(--color-primary);
  }

  @include theme('dark') {
    background-color: var(--color-bg-secondary);
  }

  .smallScreen & {
    min-width: 0;
  }

  .split.smallScreen & {
    flex: 1 1 100%;
    padding: $spacing * 2;
  }
}

.splitLeftHeader {
  display: flex;
  justify-content: flex-end;
  margin-top: -($spacing * 2);
  margin-right: -($spacing * 1.5);

  .split.smallScreen & {
    margin: 0;
  }
}

.splitLeftContent {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.splitLogo {
  height: 34px;
  margin-bottom: $spacing * 3;
}

.splitCard {
  width: 100%;
  max-width: 400px;
  background-color: var(--color-bg);
  border-radius: $radius;
  box-shadow: var(--shadow);
  padding: $spacing * 2;
}

.splitRight {
  flex: 1 1 55%;
  overflow: hidden;
}

.splitImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
</style>
